import React, { useEffect, useRef } from "react";
import { LuCheck, LuMapPin, LuNavigation } from "react-icons/lu";

const isPWA = () =>
  window.matchMedia?.("(display-mode: standalone)").matches ||
  window.navigator.standalone === true;

export default function WhereToMeetSheet({
  open,
  onClose,
  heading = "You’re all set!",
  subheading = "You’ve reserved your spot. Here’s where to meet your group when you arrive.",
  sectionTitle = "Where to meet",
  spot = "Near the front entrance by the patio.",
  note = "Look for your host or check the group chat for updates when they arrive.",
  imageUrl,
  chatNote = "Use the chat portal when the gathering starts if you need help finding the group.",
  onDirections,
}) {
  const scrollRef = useRef(0);

  useEffect(() => {
    if (!open) return;

    scrollRef.current = window.scrollY || window.pageYOffset || 0;

    document.body.style.position = "fixed";
    document.body.style.top = `-${scrollRef.current}px`;
    document.body.style.width = "100%";
    document.body.style.overflow = "hidden";

    return () => {
      document.body.style.position = "";
      document.body.style.top = "";
      document.body.style.width = "";
      document.body.style.overflow = "";
      window.scrollTo(0, scrollRef.current || 0);
    };
  }, [open]);

  if (!open) return null;

  const pwaBottomExtra = isPWA() ? 8 : 0;

  return (
    <div style={S.backdrop} onClick={onClose}>
      <div
        role="dialog"
        aria-modal="true"
        aria-label="Where to meet"
        style={S.sheet}
        onClick={(e) => e.stopPropagation()}
      >
        <div style={S.scrollArea}>
          <div style={S.topSpacer} />

          <div style={S.hero}>
            <div style={S.checkWrap}>
              <LuCheck size={17} strokeWidth={2.7} />
            </div>

            <h2 style={S.heading}>{heading}</h2>
            <p style={S.subheading}>{subheading}</p>
          </div>

          <div style={S.divider} />

          <div style={S.sectionHeader}>
            <div style={S.sectionIconWrap}>
              <LuMapPin size={16} strokeWidth={2.4} />
            </div>

            <div style={S.sectionTitle}>{sectionTitle}</div>
          </div>

          {imageUrl ? (
            <img src={imageUrl} alt="Meeting spot" style={S.meetImage} />
          ) : (
            <div style={S.meetImageFallback}>
              <LuMapPin size={28} strokeWidth={2.2} />
            </div>
          )}

          <div style={S.meetCopy}>
            <div style={S.meetSpot}>{spot}</div>
            <div style={S.meetNote}>{note}</div>
          </div>

          {chatNote ? (
            <>
              <div style={S.infoDivider} />
              <div style={S.chatNote}>{chatNote}</div>
            </>
          ) : null}
        </div>

        <div
          style={{
            ...S.actionWrap,
            paddingBottom: `calc(12px + env(safe-area-inset-bottom, 0px) + ${pwaBottomExtra}px)`,
          }}
        >
          <div style={S.actionRow}>
            <button type="button" style={S.secondaryBtn} onClick={onDirections}>
              <LuNavigation size={16} strokeWidth={2.3} />
              <span>Get directions</span>
            </button>

            <button type="button" style={S.primaryBtn} onClick={onClose}>
              Got it
            </button>
          </div>
        </div>

        <style>
          {`
            @keyframes whereToMeetUp {
              from {
                transform: translateY(16px);
                opacity: 0;
              }

              to {
                transform: translateY(0);
                opacity: 1;
              }
            }

            @media (prefers-reduced-motion: reduce) {
              * {
                animation: none !important;
                transition: none !important;
              }
            }

            button:focus-visible {
              outline: 0;
              box-shadow: 0 0 0 3px rgba(59,130,246,.35);
            }
          `}
        </style>
      </div>
    </div>
  );
}

const S = {
  backdrop: {
    position: "fixed",
    inset: 0,
    background: "rgba(0,0,0,0.42)",
    zIndex: 9999,
    display: "flex",
    alignItems: "flex-end",
    justifyContent: "center",
    padding: 8,
    fontFamily:
      "'Inter', system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
  },

  sheet: {
    width: "100%",
    maxWidth: 390,
    maxHeight: "min(650px, calc(100dvh - 16px))",
    background: "#fff",
    borderRadius: 16,
    boxShadow: "0 18px 48px rgba(0,0,0,0.22)",
    overflow: "hidden",
    animation: "whereToMeetUp 180ms ease-out",
    display: "flex",
    flexDirection: "column",
  },

  scrollArea: {
    minHeight: 0,
    flex: "1 1 auto",
    overflowY: "auto",
    WebkitOverflowScrolling: "touch",
    overscrollBehavior: "contain",
  },

  topSpacer: {
    height: 24,
    flexShrink: 0,
  },

  hero: {
    padding: "0 18px",
    textAlign: "center",
  },

  checkWrap: {
    width: 40,
    height: 40,
    borderRadius: 999,
    background: "#E7F4E3",
    color: "#111",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    margin: "0 auto 12px",
  },

  heading: {
    margin: 0,
    color: "#111",
    fontSize: 24,
    lineHeight: 1.05,
    fontWeight: 850,
    letterSpacing: "-0.04em",
  },

  subheading: {
    margin: "9px auto 0",
    maxWidth: 295,
    color: "rgba(17,17,17,0.72)",
    fontSize: 13.5,
    lineHeight: 1.35,
    fontWeight: 500,
    letterSpacing: "-0.016em",
  },

  divider: {
    height: 1,
    background: "#EEF0F2",
    margin: "17px 14px 0",
  },

  sectionHeader: {
    display: "flex",
    alignItems: "center",
    gap: 10,
    padding: "14px 16px 0",
  },

  sectionIconWrap: {
    width: 32,
    height: 32,
    borderRadius: 999,
    background: "#F7F4ED",
    color: "#111",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
  },

  sectionTitle: {
    color: "#111",
    fontSize: 15,
    lineHeight: 1.1,
    fontWeight: 820,
    letterSpacing: "-0.025em",
  },

  meetImage: {
    width: "calc(100% - 28px)",
    height: 250,
    margin: "12px 14px 0",
    objectFit: "cover",
    borderRadius: 10,
    display: "block",
  },

  meetImageFallback: {
    width: "calc(100% - 28px)",
    height: 190,
    margin: "12px 14px 0",
    borderRadius: 10,
    background: "#F4F4F5",
    color: "#111",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },

  meetCopy: {
    padding: "13px 18px 0",
  },

  meetSpot: {
    color: "#111",
    fontSize: 15,
    lineHeight: 1.18,
    fontWeight: 820,
    letterSpacing: "-0.03em",
  },

  meetNote: {
    marginTop: 7,
    color: "rgba(17,17,17,0.72)",
    fontSize: 13,
    lineHeight: 1.36,
    fontWeight: 500,
    letterSpacing: "-0.015em",
  },

  infoDivider: {
    height: 1,
    background: "#EEF0F2",
    margin: "12px 18px 0",
  },

  chatNote: {
    margin: "10px 18px 16px",
    color: "rgba(17,17,17,0.58)",
    fontSize: 12.5,
    lineHeight: 1.34,
    fontWeight: 500,
    letterSpacing: "-0.014em",
  },

  actionWrap: {
    flex: "0 0 auto",
    background: "#fff",
    borderTop: "1px solid #EEF0F2",
    paddingTop: 12,
  },

  actionRow: {
    padding: "0 14px",
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: 10,
  },

  secondaryBtn: {
    height: 44,
    borderRadius: 12,
    border: "1px solid #DADDE1",
    background: "#fff",
    color: "#111",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    gap: 8,
    fontSize: 13.5,
    lineHeight: 1,
    fontWeight: 760,
    letterSpacing: "-0.02em",
    cursor: "pointer",
  },

  primaryBtn: {
    height: 44,
    borderRadius: 12,
    border: "1px solid #111",
    background: "#111",
    color: "#fff",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: 13.5,
    lineHeight: 1,
    fontWeight: 780,
    letterSpacing: "-0.02em",
    cursor: "pointer",
  },
};